import { compact, uniq, uniqBy } from 'lodash';
import { translateToPrql } from '@gosupersimple/penguino';
import { common, model } from '@gosupersimple/types';

import { notNil, unlessNil } from '@/lib/utils';
import { exhaustiveCheck } from '@/lib/exhaustive';
import { createBasePipeline, isBasePipeline, isPipelineWithParent } from '@/core/pipeline';
import { extractCombineSteps } from '@/core/operation/combine';
import { getCellByPipelineId } from '@/core/cell/cells';
import { getPipelineById } from '@/core/exploration';
import { getPipelineByIdOrThrow } from '@/core/exploration';

import {
  AggregationType,
  Fields,
  Model,
  PipelineState,
  QueryVariables,
  CompositeFilterCondition,
  TimeAggregationPeriod,
  Pipeline,
  Exploration,
  Aggregation,
  Metric,
  Field,
  Relation,
  PipelineStateRelation,
  KeyedAggregation,
  PipelineOperation,
  FilterOperation,
  DeriveFieldOperation,
  RelationAggregateOperation,
  AddRelatedColumnOperation,
  GroupAggregateOperation,
  SwitchToRelationOperation,
  JoinPipelineOperation,
  SqlOperation,
  FieldRelation,
} from '../types';
import {
  isAggregationWithFilter,
  isExprAggregation,
  isKeyedAggregation,
  isMetricAggregation,
  isValueExpression,
} from './operation';
import {
  FieldGroupNamespace,
  FieldsOrGroups,
  suffixConflictingFields,
  flattenFieldGroups,
  getRelatedFieldSuffix,
  getUniqueFieldName,
  findFieldFromGroups,
} from './utils';
import { getModelOrThrow, getModel, getJoinKeyOnBase, getJoinKeys } from '../model/utils';
import { convertFieldsForPenguinoContext } from '../utils/penguino';
import {
  filterVariablesForPenguino,
  getVariableExpression,
  InvalidPipelineReferenceError,
} from '../utils';
import { convertPropertyValueType } from '../input';
import { getConditionField, getOperatorLabel } from '../edit-pipeline/utils/filter';

export interface PipelineStateContext {
  models: Model[];
  variables: QueryVariables;
  metrics: Metric[];
  exploration: Exploration;
}

export class InvalidOperationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'InvalidOperationError';
  }
}

export const InvalidModel = {
  modelId: 'invalid',
  name: 'Invalid model',
  primaryKey: [],
  properties: [],
  relations: [],
  labels: {},
};

const constructFieldRelation = (model: Model, key: string): FieldRelation | undefined =>
  model.primaryKey.includes(key)
    ? {
        modelId: model.modelId,
        baseModelId: model.modelId,
        name: model.name,
        key,
      }
    : unlessNil(
        model.relations.find(
          (relation) =>
            (relation.type === 'hasOne' &&
              relation.joinStrategy &&
              'joinKeyOnBase' in relation.joinStrategy &&
              key === relation.joinStrategy?.joinKeyOnBase) ??
            false,
        ),
        (relation) => {
          return relation.joinStrategy && 'joinKeyOnRelated' in relation.joinStrategy
            ? {
                modelId: relation.modelId,
                baseModelId: model.modelId,
                name: relation.name,
                key: relation.joinStrategy.joinKeyOnRelated,
              }
            : undefined;
        },
      );

const constructField = (args: {
  key: string;
  name: string;
  type: model.PropertyType | null;
  precision?: TimeAggregationPeriod;
  format?: common.PropertyValueFormat;
  model?: Model;
  keyInRelatedModel?: string;
  description?: string;
  arrayItem?: { type: model.PropertyType | null };
}) => {
  const { key, name, type, precision, format, model, keyInRelatedModel, description, arrayItem } =
    args;

  return {
    key,
    name,
    type,
    arrayItem,
    pk: model?.primaryKey.includes(key) ?? false,
    model:
      model === undefined
        ? undefined
        : { modelId: model.modelId, name: model.name, propertyKey: keyInRelatedModel ?? key },
    relation:
      model === undefined ? undefined : constructFieldRelation(model, keyInRelatedModel ?? key),
    precision,
    format,
    description,
  };
};

export const getFieldsWithRelatedFields = (
  state: PipelineState,
  models: Model[],
): FieldsOrGroups => {
  const relatedFields = state.relations
    .filter((relation) => relation.type === 'hasOne' || relation.type === 'hasOneThrough')
    .map((relation) => {
      const relatedModel = getModel(models, relation.modelId);
      if (!relatedModel) {
        return undefined;
      }

      return {
        key: `${relation.baseModelId}.${relation.key}`,
        name: relation.name,
        fields: relatedModel.properties.map((field) => ({
          ...field,
          model: { modelId: relatedModel.modelId, name: relatedModel.name, propertyKey: field.key },
          relation: {
            modelId: relation.modelId,
            baseModelId: relation.baseModelId,
            name: relation.name,
            key: relation.key,
          },
        })),
      };
    })
    .filter(notNil);

  return [...state.fields.map((field) => ({ ...field, relation: undefined })), ...relatedFields];
};

/**
 * Find the (possibly renamed) field that is the given property of the given model.
 * State relations are calculated purely on model properties to avoid flooding the state with
 * relations due to matching field names.
 */
export const findModelPropertyField = (fields: Field[], modelId: string, propertyKey: string) =>
  fields.find(
    (field) => field.model?.modelId === modelId && field.model?.propertyKey === propertyKey,
  );

/**
 * Find the field suitable for joining a relation
 * Prefer model property fields to support renamed fields but fall back to matching by key to support derived fields.
 */
export const findRelationJoinKey = (fields: Field[], modelId: string, joinKey: string) =>
  findModelPropertyField(fields, modelId, joinKey) ?? fields.find((field) => field.key === joinKey);

/**
 * Find the field suitable for joining a custom join strategy.
 * Only match by key since the join keys are explicitly picked.
 */
export const findCustomJoinKey = (fields: Field[], joinKey: string) =>
  fields.find((field) => field.key === joinKey);

const filterOperation = (
  state: PipelineState,
  operation: FilterOperation,
  _operationIndex: number,
  ctx: PipelineStateContext,
) => {
  const { models, variables } = ctx;
  validateFilterCondition(
    operation.parameters,
    getFieldsWithRelatedFields(state, models),
    state.fields,
    variables,
  );
  return state;
};

const deriveFieldOperation = (
  state: PipelineState,
  operation: DeriveFieldOperation,
  _operationIndex: number,
  ctx: PipelineStateContext,
) => {
  const { variables } = ctx;
  const { returnType, format, precision } = getExpressionResult(
    operation.parameters.value.expression,
    state.fields,
    variables,
  );

  const fields = [
    ...state.fields,
    {
      key: operation.parameters.key,
      name: operation.parameters.fieldName,
      // Ignore Enum types from expressions for now. Since we do not have the lineage, anything downstream
      // does not know how to fetch enum options.
      type: convertPropertyValueType(returnType === 'Enum' ? 'String' : returnType) ?? 'String',
      format,
      precision,
    },
  ];

  return { ...state, fields };
};

export const isRelationGrouping = (group: common.Grouping): group is common.RelationGrouping =>
  'relation' in group &&
  group.relation !== undefined &&
  'property' in group &&
  group.property !== undefined;

export const getGroupingOutputKey = (group: common.Grouping): string =>
  isRelationGrouping(group) ? group.property.key : group.key;

const groupAggregateOperation = (
  state: PipelineState,
  operation: GroupAggregateOperation,
  _operationIndex: number,
  ctx: PipelineStateContext,
) => {
  const { models, variables, metrics } = ctx;
  const { groups, aggregations, slice } = operation.parameters;
  const grouping = groups.map(({ key }) => ({ key }));
  const fieldsWithRelated = getFieldsWithRelatedFields(state, models);

  validateSlice(slice, state.fields);
  validateGroupDependencies(groups, fieldsWithRelated);

  const groupFields = groups.map((grouping) => {
    const { key, precision } = grouping;
    const { relation, property } = isRelationGrouping(grouping) ? grouping : {};

    const field = findFieldFromGroups(fieldsWithRelated, { key, relation });
    if (field === undefined) {
      throw new InvalidOperationError(`Field "${key}" not found`);
    }

    return constructField({
      key: property?.key ?? key,
      name: field.name,
      type: precision === 'day_of_week' ? 'Integer' : field.type,
      precision: precisionToTimeInterval(precision, variables),
      format: precision === 'day_of_week' ? 'date' : field.format,
      model: getModel(models, field.model?.modelId ?? ''),
      keyInRelatedModel: key,
      description: field.description,
    });
  });

  if (aggregations.length === 0) {
    return {
      ...state,
      fields: [
        ...groupFields,
        ...state.fields.filter(
          (field) => !groupFields.some((groupField) => groupField.key === field.key),
        ),
      ],
      grouping,
    };
  }

  const fields = [
    ...groupFields,
    ...aggregations.map((aggregation) => {
      const field = isKeyedAggregation(aggregation)
        ? findFieldFromGroups(fieldsWithRelated, aggregation)
        : undefined;

      if (isKeyedAggregation(aggregation) && field === undefined) {
        throw new InvalidOperationError(`Field "${aggregation.key}" not found`);
      }

      if (isAggregationWithFilter(aggregation)) {
        validateFilterCondition(aggregation.filter, fieldsWithRelated, state.fields, variables);
      }

      return constructField({
        key: aggregation.property.key,
        name: aggregation.property.name,
        type: getResultTypeForAggregation(aggregation, fieldsWithRelated, variables, metrics),
        format: getResultFormatForAggregation(aggregation, fieldsWithRelated, ctx),
        model:
          aggregation.type === 'first' ? getModel(models, field?.model?.modelId ?? '') : undefined,
        keyInRelatedModel:
          aggregation.type === 'first' ? (field?.model?.propertyKey ?? aggregation.key) : undefined,
      });
    }),
  ];

  return { ...state, fields, grouping };
};

const addRelatedColumnOperation = (
  state: PipelineState,
  operation: AddRelatedColumnOperation,
  _operationIndex: number,
  ctx: PipelineStateContext,
) => {
  const { models } = ctx;
  const { relatedModel, relation } = getRelatedModelOrThrow(
    state,
    models,
    operation.parameters.relation.key,
    operation.parameters.relation.modelId,
  );

  if (!['hasOne', 'hasOneThrough'].includes(relation.type)) {
    throw new InvalidOperationError(`Cannot add column from ${relation.name}`);
  }

  const relatedPipeline =
    operation.parameters.pipelineId === undefined
      ? createBasePipeline({
          baseModelId: relatedModel.modelId,
          operations: [],
        })
      : getPipelineByIdOrThrow(operation.parameters.pipelineId, ctx.exploration);
  const relatedPipelineFields = getFinalState(relatedPipeline, ctx).fields;

  const newFields = operation.parameters.columns.map((column) => {
    const relatedField = relatedPipelineFields.find((field) => field.key === column.key);
    if (relatedField === undefined) {
      throw new InvalidOperationError(`Field "${column.key}" not found`);
    }

    return constructField({
      key: column.property.key,
      name: column.property.name,
      type: relatedField.type,
      precision: relatedField?.precision,
      format: relatedField?.format,
      model: getModel(models, relatedField?.model?.modelId ?? relatedModel.modelId),
      keyInRelatedModel: relatedField?.model?.propertyKey ?? column.key,
      description: relatedField?.description,
    });
  });

  const fields = state.fields.concat(newFields);

  return {
    ...state,
    fields,
  };
};

const relationAggregateOperation = (
  state: PipelineState,
  operation: RelationAggregateOperation,
  _operationIndex: number,
  ctx: PipelineStateContext,
) => {
  const { models, variables, metrics } = ctx;
  const { parameters } = operation;

  const relation =
    'relation' in parameters
      ? state.relations.find((relation) => relation.key === parameters.relation.key)
      : undefined;

  const relatedPipeline =
    'relation' in parameters
      ? parameters.pipelineId === undefined
        ? createBasePipeline({
            baseModelId: findValidRelation(
              state.relations,
              models,
              parameters.relation.key,
              parameters.relation.modelId,
            ).modelId,
            operations: [],
          })
        : getPipelineByIdOrThrow(parameters.pipelineId, ctx.exploration)
      : getPipelineByIdOrThrow(parameters.pipelineId, ctx.exploration);

  const relatedPipelineState = getFinalState(relatedPipeline, ctx);
  const relatedPipelineFields = relatedPipelineState.fields;
  const extendedRelatedPipelineFields = getFieldsWithRelatedFields(relatedPipelineState, models);

  // Validate that the join keys exist
  const joinStrategy = 'joinStrategy' in parameters ? parameters.joinStrategy : undefined;
  if (joinStrategy !== undefined) {
    const { joinKeyOnBase, joinKeyOnRelated } = joinStrategy;
    const baseField = findCustomJoinKey(state.fields, joinKeyOnBase);
    if (baseField === undefined) {
      throw new InvalidOperationError(`Could not find base column ${joinKeyOnBase}`);
    }
    const relatedField = findCustomJoinKey(relatedPipelineFields, joinKeyOnRelated);
    if (relatedField === undefined) {
      throw new InvalidOperationError(`Could not find related column ${joinKeyOnRelated}`);
    }
  } else if (relation !== undefined) {
    const joinKeys = getJoinKeys(relation, state.relations, models);
    if (joinKeys !== null) {
      const { joinKeyOnBase, joinKeyOnRelated } = joinKeys;
      const baseField = findRelationJoinKey(state.fields, relation.baseModelId, joinKeyOnBase);
      if (baseField === undefined) {
        throw new InvalidOperationError(`Could not find base column ${joinKeyOnBase}`);
      }
      const relatedField = findRelationJoinKey(
        relatedPipelineFields,
        relation.modelId,
        joinKeyOnRelated,
      );
      if (relatedField === undefined) {
        throw new InvalidOperationError(`Could not find related column ${joinKeyOnRelated}`);
      }
    }
  } else {
    throw new InvalidOperationError(`Either relation or join strategy must be provided`);
  }

  validateSlice(operation.parameters.slice, relatedPipelineFields);
  if (operation.parameters.filter !== undefined) {
    validateFilterCondition(
      operation.parameters.filter,
      extendedRelatedPipelineFields,
      [
        ...state.fields,
        { key: FieldGroupNamespace.BaseModel, fields: state.fields },
        {
          key: FieldGroupNamespace.RelatedModel,
          fields: relatedPipelineFields,
        },
      ],
      variables,
    );
  }

  const fields = state.fields.concat(
    operation.parameters.aggregations.map((aggregation) => {
      const relatedField = relatedPipelineFields.find(
        (field) => isKeyedAggregation(aggregation) && field.key === aggregation.key,
      );

      if (isKeyedAggregation(aggregation) && relatedField === undefined) {
        throw new InvalidOperationError(`Field "${aggregation.key}" not found`);
      }

      if (isAggregationWithFilter(aggregation)) {
        validateFilterCondition(
          aggregation.filter,
          extendedRelatedPipelineFields,
          relatedPipelineFields,
          variables,
        );
      }

      return constructField({
        key: aggregation.property.key,
        name: aggregation.property.name,
        type: getResultTypeForAggregation(aggregation, relatedPipelineFields, variables, metrics),
        format: getResultFormatForAggregation(aggregation, relatedPipelineFields, ctx),
        model:
          aggregation.type === 'first'
            ? getModel(models, relatedField?.model?.modelId ?? '')
            : undefined,
        keyInRelatedModel:
          aggregation.type === 'first'
            ? (relatedField?.model?.propertyKey ?? aggregation.key)
            : undefined,
        arrayItem:
          aggregation.type === 'array'
            ? {
                type:
                  relatedPipelineFields.find(({ key }) => key === aggregation.key)?.type ?? null,
              }
            : undefined,
      });
    }),
  );

  return { ...state, fields };
};

const switchToRelationOperation = (
  state: PipelineState,
  operation: SwitchToRelationOperation,
  _operationIndex: number,
  ctx: PipelineStateContext,
) => {
  const { models } = ctx;
  const { relatedModel } = getRelatedModelOrThrow(
    state,
    models,
    operation.parameters.relation.key,
    operation.parameters.relation.modelId,
  );
  return {
    ...state,
    model: relatedModel,
    fields: relatedModel.properties.map(({ key, name, type, format, description }) =>
      constructField({ key, name, type, format, model: relatedModel, description }),
    ),
  };
};

const joinPipelineOperation = (
  state: PipelineState,
  operation: JoinPipelineOperation,
  operationIndex: number,
  ctx: PipelineStateContext,
) => {
  const { exploration } = ctx;
  const pipeline =
    state.pipelineId !== undefined
      ? getCellByPipelineId(exploration.view.cells, state.pipelineId)?.pipeline
      : undefined;
  const states = extractCombineSteps(operation).reduce(
    (memo, step, i, items) => {
      const joinStrategy = step.joinStrategy;
      if (joinStrategy === undefined) {
        throw new InvalidOperationError(`Join strategy not found for step ${i}`);
      }

      const rightPipelineState = getFinalState(step.pipeline, ctx);

      const leftFieldKeys = memo.flatMap((state) => state.fields).map((f) => f.key);
      const rightSideFields = rightPipelineState.fields.filter(
        (field) =>
          !(
            joinStrategy.joinKeyOnBase === joinStrategy.joinKeyOnRelated &&
            field.key === joinStrategy.joinKeyOnRelated
          ),
      );
      if (!leftFieldKeys.includes(joinStrategy.joinKeyOnBase)) {
        throw new InvalidOperationError(`Field "${joinStrategy.joinKeyOnBase}" not found`);
      }
      if (!rightPipelineState.fields.some((field) => field.key === joinStrategy.joinKeyOnRelated)) {
        throw new InvalidOperationError(`Field "${joinStrategy.joinKeyOnRelated}" not found`);
      }
      // This must mimick backend's field key & name renaming convention exactly
      const rightFieldMap = new Map(
        rightSideFields.map((f) => {
          if (!leftFieldKeys.includes(f.key)) {
            return [f.key, f.key];
          }

          // Use stepId if available
          if ('stepId' in step && step.stepId !== undefined) {
            return [f.key, `${f.key}_${step.stepId}`];
          }

          // Old format:
          return [f.key, `join_${operationIndex}${items.length > 1 ? `_${i}` : ''}_r_${f.key}`];
        }),
      );

      const fields = rightSideFields.map((field) => ({
        ...field,
        key: rightFieldMap.get(field.key) ?? field.key,
      }));

      return [
        ...memo,
        {
          ...rightPipelineState,
          fields,
          pipelineId: 'parentId' in step.pipeline ? step.pipeline.parentId : undefined,
        },
      ];
    },
    [
      {
        ...state,
        pipelineId:
          pipeline !== undefined && 'parentId' in pipeline
            ? pipeline.parentId
            : pipeline?.pipelineId,
      },
    ],
  );

  // First suffix conflicting field names across all joined pipelines
  const renamedFields = states.flatMap((s) => {
    const otherFields = states.filter((other) => other !== s).flatMap((other) => other.fields);
    return suffixConflictingFields(
      otherFields,
      s.fields,
      getRelatedFieldSuffix(s.pipelineId, exploration, {
        modelName: s.model.name,
      }),
    );
  });

  // Ensure name uniqueness across all fields
  return {
    ...state,
    fields: renamedFields.reduce<Field[]>((acc, field) => {
      return [...acc, { ...field, name: getUniqueFieldName(acc, field.name) }];
    }, []),
  };
};

const sqlOperation = (
  state: PipelineState,
  operation: SqlOperation,
  _operationIndex: number,
  _ctx: PipelineStateContext,
) => {
  const sqlModel: Model = {
    modelId: 'sql-model',
    name: 'Ad-Hoc model from SQL',
    primaryKey: [],
    properties: uniqBy(operation.parameters.fields ?? [], 'key').map(({ key, type }) => ({
      key,
      name: key,
      type: type ?? 'String',
    })),
    relations: [],
    labels: {},
  };

  return { ...state, model: sqlModel, fields: sqlModel.properties };
};

export const getNextPipelineStateOrThrow = (
  state: PipelineState,
  operation: PipelineOperation,
  operationIndex: number,
  ctx: PipelineStateContext,
): Omit<PipelineState, 'relations'> => {
  if (operation.disabled === true) {
    return state;
  }
  switch (operation.operation) {
    case 'filter':
      return filterOperation(state, operation, operationIndex, ctx);
    case 'deriveField':
      return deriveFieldOperation(state, operation, operationIndex, ctx);
    case 'groupAggregate':
      return groupAggregateOperation(state, operation, operationIndex, ctx);
    case 'addRelatedColumn':
      return addRelatedColumnOperation(state, operation, operationIndex, ctx);
    case 'relationAggregate':
      return relationAggregateOperation(state, operation, operationIndex, ctx);
    case 'switchToRelation':
      return switchToRelationOperation(state, operation, operationIndex, ctx);
    case 'joinPipeline':
      return joinPipelineOperation(state, operation, operationIndex, ctx);
    case 'sql':
      return sqlOperation(state, operation, operationIndex, ctx);
    default:
      return state;
  }
};

export const getNextPipelineState = (
  state: PipelineState,
  operation: PipelineOperation,
  operationIndex: number,
  ctx: PipelineStateContext,
): Omit<PipelineState, 'relations'> => {
  try {
    return getNextPipelineStateOrThrow(state, operation, operationIndex, ctx);
  } catch (e) {
    if (!(e instanceof InvalidOperationError) && !(e instanceof InvalidPipelineReferenceError)) {
      throw e;
    }
    return state;
  }
};

const getFieldTypeByAggregationType = (type: AggregationType) => {
  switch (type) {
    case 'count':
      return 'Integer';
    default:
      return null;
  }
};

export const getPipelineStateAtIndexOrThrow = (
  pipeline: Pipeline,
  index: number,
  ctx: PipelineStateContext,
): PipelineState => {
  const baseState = isPipelineWithParent(pipeline)
    ? getFinalState(getPipelineByIdOrThrow(pipeline.parentId, ctx.exploration), ctx)
    : getBaseState(pipeline.baseModelId, ctx.models, pipeline.pipelineId);
  const state = pipeline.operations.slice(0, index).reduce((state, operation, i) => {
    const nextState = getNextPipelineStateOrThrow(state, operation, i, ctx);
    return { ...nextState, relations: getAvailableRelations(nextState.fields, ctx.models) };
  }, baseState);

  return state;
};

export const getInitialState = (pipeline: Pipeline, ctx: PipelineStateContext) => {
  const parentPipeline = isPipelineWithParent(pipeline)
    ? getPipelineById(pipeline.parentId, ctx.exploration)
    : undefined;
  return parentPipeline !== undefined
    ? getFinalState(parentPipeline, ctx)
    : getBaseState(
        isBasePipeline(pipeline) ? pipeline.baseModelId : '',
        ctx.models,
        pipeline.pipelineId,
      );
};

export const getPipelineStateAtIndex = (
  pipeline: Pipeline,
  index: number,
  ctx: PipelineStateContext,
): PipelineState => {
  const initialState = getInitialState(pipeline, ctx);
  return pipeline.operations.slice(0, index).reduce((state, operation, i) => {
    const nextState = getNextPipelineState(state, operation, i, ctx);
    return { ...nextState, relations: getAvailableRelations(nextState.fields, ctx.models) };
  }, initialState);
};

export const getBaseStateOrThrow = (
  baseModelId: string,
  models: Model[],
  pipelineId?: string,
): PipelineState => {
  const baseModel = getModelOrThrow(models, baseModelId);
  const fields = baseModel.properties.map(({ key, name, type, format, description }) =>
    constructField({ key, name, type, format, model: baseModel, description }),
  );
  return {
    model: baseModel,
    relations: getAvailableRelations(fields, models),
    fields,
    pipelineId,
    grouping: [],
  };
};

export const getBaseState = (
  baseModelId: string,
  models: Model[],
  pipelineId?: string,
): PipelineState => {
  const baseModel = getModel(models, baseModelId);

  if (baseModel === undefined) {
    return {
      model: InvalidModel,
      relations: [],
      fields: [],
      pipelineId,
      grouping: [],
    };
  }

  const fields = baseModel.properties.map(({ key, name, type, format, description }) =>
    constructField({ key, name, type, format, model: baseModel, description }),
  );

  return {
    model: baseModel,
    relations: getAvailableRelations(fields, models),
    fields,
    pipelineId,
    grouping: [],
  };
};

export const getFinalStateOrThrow = (
  pipeline: Pipeline,
  ctx: PipelineStateContext,
): PipelineState => getPipelineStateAtIndexOrThrow(pipeline, pipeline.operations.length, ctx);

export const getFinalState = (pipeline: Pipeline, ctx: PipelineStateContext): PipelineState =>
  getPipelineStateAtIndex(pipeline, pipeline.operations.length, ctx);

export const getPipelineFields = (pipeline: Pipeline, ctx: PipelineStateContext) =>
  getFinalStateOrThrow(pipeline, ctx).fields;

const getAvailableRelations = (fields: Field[], models: Model[]) =>
  uniq(compact(fields.map((field) => field.model?.modelId))).flatMap((modelId) => {
    const model = getModel(models, modelId);
    if (model === undefined) {
      throw new InvalidOperationError(
        `Model "${modelId}" not found in ${models.map((m) => m.modelId).join(', ')}`,
      );
    }
    return (
      model.relations
        ?.filter((relation) => joinKeyOnBaseExists(fields, relation, model))
        .map((relation) => ({ ...relation, baseModelId: model.modelId })) ?? []
    );
  });

export const findValidRelation = (
  relations: PipelineStateRelation[],
  models: Model[],
  relationKey: string,
  baseModelId?: string,
) => {
  const matchingRelations = relations.filter(
    (r) => r.key === relationKey && (r.baseModelId === baseModelId || baseModelId === undefined),
  );
  if (matchingRelations.length === 0) {
    throw new InvalidOperationError(
      `Relation "${relationKey}" not found${baseModelId !== undefined ? ` in model ${baseModelId}` : ''}`,
    );
  }
  if (matchingRelations.length > 1) {
    throw new InvalidOperationError(
      `Relation key "${relationKey}" is ambiguous. Please provide a model ID.`,
    );
  }
  const relatedModel = getModel(models, matchingRelations[0].modelId);
  if (relatedModel === undefined) {
    throw new InvalidOperationError(`Model "${matchingRelations[0].modelId}" not found`);
  }
  return matchingRelations[0];
};

const joinKeyOnBaseExists = (fields: Field[], relation: Relation, model: Model) => {
  const { joinStrategy } = relation;
  if (joinStrategy !== undefined && 'sql' in joinStrategy) {
    return fields.some(({ key }) =>
      new RegExp(`{{base_model}}\\.${key}\\b`).test(joinStrategy.sql),
    );
  }
  const joinKey = getJoinKeyOnBase(relation, model);
  if (joinKey === null) {
    return false;
  }
  return findModelPropertyField(fields, model.modelId, joinKey) !== undefined;
};

const getRelatedModelOrThrow = (
  state: PipelineState,
  models: Model[],
  relationKey: string,
  modelId?: string,
) => {
  const relation = findValidRelation(state.relations, models, relationKey, modelId);
  return { relatedModel: getModelOrThrow(models, relation.modelId), relation };
};

const validateFilterCondition = (
  condition: CompositeFilterCondition,
  fields: FieldsOrGroups,
  fieldsForExpression: FieldsOrGroups,
  variables: QueryVariables,
): boolean => {
  if (condition.operator === 'and' || condition.operator === 'or') {
    return condition.operands.every((c) =>
      validateFilterCondition(c, fields, fieldsForExpression, variables),
    );
  } else if (!('key' in condition)) {
    return true;
  }

  const field = getConditionField(condition, fields);

  if (field === undefined) {
    throw new InvalidOperationError(`Field "${condition.key}" not found`);
  }

  if (field.type === 'Date' && (condition.operator === 'in' || condition.operator === 'notin')) {
    throw new InvalidOperationError(
      `Operator "${getOperatorLabel(condition.operator, field.type)}" is not supported for Date fields`,
    );
  }

  if (isValueExpression(condition.value)) {
    const { returnType } = getExpressionResult(
      condition.value.expression,
      fieldsForExpression,
      variables,
    );
    const expectedType =
      condition.operator === 'in' || condition.operator === 'notin' ? 'Array' : null;
    const typesMatch = expectedType === null || returnType === expectedType;
    if (!typesMatch) {
      throw new InvalidOperationError(`Expected type ${expectedType}, got ${returnType}`);
    }
  }

  return true;
};

const validateSlice = (slice: common.Slice | undefined, fields: Fields) => {
  if (slice === undefined) {
    return true;
  }
  if (slice.limit !== undefined && slice.limit < 0) {
    throw new InvalidOperationError('Slice limit must be a positive number');
  }
  if (slice.offset !== undefined && slice.offset < 0) {
    throw new InvalidOperationError('Slice offset must be a positive number');
  }
  validateSorting(slice?.sort, fields);
};

const validateSorting = (sorting: common.Sorting[] | undefined, fields: Fields) => {
  if (sorting === undefined) {
    return true;
  }
  sorting.forEach((sort) => {
    if (!fields.some((field) => field.key === sort.key)) {
      throw new InvalidOperationError(`Field "${sort.key}" not found`);
    }
  });
};

const validateGroupDependencies = (grouping: common.Grouping[], fields: FieldsOrGroups) => {
  grouping.forEach((g) => {
    const field = findFieldFromGroups(fields, g);
    if (field === undefined) {
      throw new InvalidOperationError(`Field "${g.key}" not found`);
    }
  });
};

const getExpressionResult = (
  expression: string,
  fields: FieldsOrGroups,
  variables: QueryVariables,
) => {
  try {
    const { returnType, format, precision } = translateToPrql(expression, {
      fields: convertFieldsForPenguinoContext(fields),
      variables: filterVariablesForPenguino(variables),
    });
    return { returnType: convertPropertyValueType(returnType), format, precision };
  } catch (e) {
    if (e instanceof Error) {
      throw new InvalidOperationError(e.message);
    }
    throw new InvalidOperationError(`Invalid expression "${expression}"`);
  }
};

const getResultTypeForAggregation = (
  aggregation: Aggregation,
  fields: FieldsOrGroups,
  variables: QueryVariables,
  metrics: Metric[],
) => {
  return (
    getFieldTypeByAggregationType(aggregation.type) ??
    (isKeyedAggregation(aggregation)
      ? getResultTypeforKeyedAggregation(aggregation, fields)
      : isExprAggregation(aggregation)
        ? (convertPropertyValueType(
            getExpressionResult(aggregation.value.expression, fields, variables).returnType,
          ) ?? ('String' as const))
        : isMetricAggregation(aggregation)
          ? getResultTypeForMetric(aggregation.metricId, metrics)
          : ('String' as const))
  );
};

const getResultFormatForAggregation = (
  aggregation: Aggregation,
  fields: FieldsOrGroups,
  ctx: PipelineStateContext,
) => {
  switch (aggregation.type) {
    case 'sum':
    case 'avg':
    case 'min':
    case 'max':
    case 'first':
    case 'last':
    case 'median': {
      const field = findFieldFromGroups(fields, aggregation);
      if (field === undefined) {
        throw new InvalidOperationError(`Field "${aggregation.key}" not found`);
      }
      return field.format;
    }
    case 'metric': {
      const metric = ctx.metrics.find((m) => m.metricId === aggregation.metricId);
      if (metric === undefined) {
        throw new InvalidOperationError(`Metric "${aggregation.metricId}" not found`);
      }
      const state = getFinalState(
        createBasePipeline({
          baseModelId: metric.definition.modelId,
          operations: metric.definition.operations ?? [],
        }),
        ctx,
      );
      return getResultFormatForAggregation(
        { ...metric.definition.aggregation, property: aggregation.property },
        state.fields,
        ctx,
      );
    }
    default:
      return undefined;
  }
};

const getResultTypeforKeyedAggregation = (
  aggregation: KeyedAggregation,
  fields: FieldsOrGroups,
) => {
  const type = aggregation.type;
  switch (type) {
    case 'count_distinct':
      return 'Integer' as const;
    case 'sum':
    case 'min':
    case 'max':
    case 'first':
    case 'last':
      return (
        flattenFieldGroups(fields).find((field) => field.key === aggregation.key)?.type ?? null
      );
    case 'avg':
    case 'median':
      return 'Float';
    case 'array':
      return 'Array' as const;
    default:
      exhaustiveCheck(type);
  }
};

const getResultTypeForMetric = (metricId: string, metrics: Metric[]) => {
  if (!metrics.some((m) => m.metricId === metricId)) {
    throw new InvalidOperationError(`Metric "${metricId}" not found`);
  }
  return 'Number' as const;
};

export const precisionToTimeInterval = (
  precision: common.TimeInterval | common.ExpressionValue | undefined,
  variables: common.QueryVariables,
) => {
  if (precision === undefined) {
    return undefined;
  }

  const timeIntervalValue = isValueExpression(precision)
    ? variables.find(({ key }) => getVariableExpression(key) === precision.expression)?.value
    : precision;
  const validation = common.timeInterval.safeParse(timeIntervalValue);
  if (!validation.success) {
    throw new InvalidOperationError(
      `Invalid precision value${timeIntervalValue !== '' ? `: ${timeIntervalValue}` : ''}`,
    );
  }
  return validation.data;
};
